import { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Trash2, Edit, Package, Search, Filter, Cloud, Loader2, Download, Check, RefreshCw, Upload, AlertTriangle, ShoppingCart, Clock, Barcode, TrendingUp, Calculator, History, Power, PowerOff, Copy } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { searchMatch } from '@/utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterIncompletos, setFilterIncompletos] = useState(false);
  const [filterAtivo, setFilterAtivo] = useState('todos'); // 'todos', 'ativos', 'inativos'
  const [filterSemMadeira, setFilterSemMadeira] = useState(false); // Filtro para produtos sem controle de madeira
  const [vendasPorProduto, setVendasPorProduto] = useState({});
  const { isAdmin } = useAuth();
  const [importingOmie, setImportingOmie] = useState(false);
  const [omieStatus, setOmieStatus] = useState(null);
  
  // Estados para modal de importação seletiva
  const [omieDialogOpen, setOmieDialogOpen] = useState(false);
  const [omieProdutos, setOmieProdutos] = useState([]);
  const [omieLoading, setOmieLoading] = useState(false);
  const [omieSelecionados, setOmieSelecionados] = useState([]);
  const [omieSearchTerm, setOmieSearchTerm] = useState('');
  const [omiePagina, setOmiePagina] = useState(1);
  const [omieTotalPaginas, setOmieTotalPaginas] = useState(1);
  const [omieTotal, setOmieTotal] = useState(0);
  const [syncingProductId, setSyncingProductId] = useState(null);

  const [formData, setFormData] = useState({
    nome: '',
    codigo: '', // SKU / Código de barras
    descricao: '',
    categoria: '',
    preco_base: '',
    custo: '',
    estoque: '0',
    observacoes: '',
    ncm: '94036000',
    volume_m3: '',
    tipo_madeira_id: '',
    // Campos para cálculo de custo
    pecas_madeira: [], // Array de peças: {comprimento, largura, espessura, quantidade}
    mao_de_obra: '',
    pintura: '',
    insumos: '',
    tempo_producao_horas: '', // Tempo de produção em horas
    // Novos campos
    ativo: true, // Produto ativo/inativo
    margem_desejada: '30', // Margem padrão 30%
    unidade: 'UN',
    nao_usa_madeira: false // Produto não utiliza madeira
  });

  // Custo hora de mão de obra (carregado do backend)
  const [custoHora, setCustoHora] = useState(0);

  // Tipos de madeira para o select
  const [tiposMadeira, setTiposMadeira] = useState([]);
  
  // Estado para nova peça sendo adicionada
  const [novaPeca, setNovaPeca] = useState({
    comprimento: '',
    largura: '',
    espessura: '',
    quantidade: '1'
  });

  // Estados para Variações do Produto
  const [variacoes, setVariacoes] = useState([]);
  const [variacaoDialogOpen, setVariacaoDialogOpen] = useState(false);
  const [editingVariacao, setEditingVariacao] = useState(null);
  const [variacaoForm, setVariacaoForm] = useState({
    nome: '',
    descricao: '',
    ativo: true,
    tipo: 'adicional',
    valor_adicional: '',
    percentual_adicional: '',
    tipo_madeira_id: '',
    pecas_madeira: [],
    tempo_adicional_horas: '',
    mao_obra_adicional: '',
    pintura_adicional: '',
    insumos_adicionais: '',
    ferragens_adicionais: '',
    outros_custos: '',
    observacoes: ''
  });
  const [novaPecaVariacao, setNovaPecaVariacao] = useState({
    comprimento: '',
    largura: '',
    espessura: '',
    quantidade: '1'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes, tiposMadeiraRes, vendasRes, custoHoraRes] = await Promise.all([
        axios.get(`${API}/products`, { params: { all_data: true } }),
        axios.get(`${API}/settings/categories/product`),
        axios.get(`${API}/tipos-madeira`),
        axios.get(`${API}/products/vendas`),
        axios.get(`${API}/custos/mao-de-obra`).catch(() => ({ data: { custo_hora: 0 } }))
      ]);
      const productsData = Array.isArray(productsRes.data) ? productsRes.data : (productsRes.data.products || []);
      setProducts(productsData);
      setCategories(categoriesRes.data.categories || []);
      setTiposMadeira(tiposMadeiraRes.data || []);
      setVendasPorProduto(vendasRes.data || {});
      setCustoHora(custoHoraRes.data?.custo_hora || 0);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Verificar status do Omie
  const checkOmieStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/omie/status`);
      setOmieStatus(response.data);
    } catch (error) {
      setOmieStatus(null);
    }
  }, []);

  useEffect(() => {
    checkOmieStatus();
  }, [checkOmieStatus]);

  // Importar produtos do Omie
  const handleImportOmie = async (importarTodos = false) => {
    setImportingOmie(true);
    try {
      const url = importarTodos 
        ? `${API}/omie/produtos/importar?importar_todos=true&registros=100`
        : `${API}/omie/produtos/importar?registros=100`;
      const response = await axios.post(url);
      if (response.data.success) {
        const msg = importarTodos 
          ? `Importação completa! ${response.data.importados} novos, ${response.data.atualizados} atualizados (${response.data.paginas_processadas} páginas)`
          : `Importação concluída! ${response.data.importados} novos, ${response.data.atualizados} atualizados`;
        toast.success(msg);
        fetchData();
      } else {
        toast.error('Erro ao importar produtos');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar produtos do Omie');
    } finally {
      setImportingOmie(false);
    }
  };

  // Carregar produtos do Omie para seleção
  const carregarProdutosOmie = async (pagina = 1, busca = '') => {
    setOmieLoading(true);
    // Limpar resultados anteriores quando inicia nova busca
    if (busca) {
      setOmieProdutos([]);
      setOmieTotal(0);
    }
    try {
      const url = busca 
        ? `${API}/omie/produtos?busca=${encodeURIComponent(busca)}`
        : `${API}/omie/produtos?pagina=${pagina}&registros=50`;
      const response = await axios.get(url, { timeout: 120000 }); // 2 min timeout para busca
      if (response.data.success) {
        setOmieProdutos(response.data.data.produto_servico_cadastro || []);
        setOmieTotalPaginas(response.data.data.total_de_paginas || 1);
        setOmieTotal(response.data.data.total_de_registros || 0);
        setOmiePagina(busca ? 1 : pagina);
      }
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      toast.error('Erro ao carregar produtos do Omie');
    } finally {
      setOmieLoading(false);
    }
  };

  // Buscar produtos do Omie (debounce)
  const [omieSearchTimeout, setOmieSearchTimeout] = useState(null);
  const handleOmieSearch = (termo) => {
    setOmieSearchTerm(termo);
    
    // Limpar timeout anterior
    if (omieSearchTimeout) {
      clearTimeout(omieSearchTimeout);
    }
    
    // Se termo vazio, voltar para listagem paginada
    if (!termo || termo.length < 2) {
      carregarProdutosOmie(1, '');
      return;
    }
    
    // Debounce: aguardar 500ms antes de buscar
    const timeout = setTimeout(() => {
      carregarProdutosOmie(1, termo);
    }, 500);
    setOmieSearchTimeout(timeout);
  };

  // Abrir modal de seleção
  const abrirModalOmie = () => {
    setOmieDialogOpen(true);
    setOmieSelecionados([]);
    setOmieSearchTerm('');
    carregarProdutosOmie(1);
  };

  // Toggle seleção de produto
  const toggleSelecionado = (codigoProduto) => {
    setOmieSelecionados(prev => 
      prev.includes(codigoProduto) 
        ? prev.filter(c => c !== codigoProduto)
        : [...prev, codigoProduto]
    );
  };

  // Selecionar/Desselecionar todos da página
  const toggleSelecionarTodos = () => {
    const codigosPagina = omieProdutos.map(p => p.codigo_produto);
    const todosSelecionados = codigosPagina.every(c => omieSelecionados.includes(c));
    
    if (todosSelecionados) {
      setOmieSelecionados(prev => prev.filter(c => !codigosPagina.includes(c)));
    } else {
      setOmieSelecionados(prev => [...new Set([...prev, ...codigosPagina])]);
    }
  };

  // Importar produtos selecionados
  const importarSelecionados = async () => {
    if (omieSelecionados.length === 0) {
      toast.error('Selecione pelo menos um produto');
      return;
    }
    
    setImportingOmie(true);
    try {
      const response = await axios.post(`${API}/omie/produtos/importar-selecionados`, {
        codigos: omieSelecionados
      });
      if (response.data.success) {
        toast.success(`${response.data.importados} produtos importados, ${response.data.atualizados} atualizados`);
        setOmieDialogOpen(false);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar produtos');
    } finally {
      setImportingOmie(false);
    }
  };

  // Sincronizar produto local -> Omie
  const handleSyncToOmie = async (productId) => {
    setSyncingProductId(productId);
    try {
      const response = await axios.post(`${API}/omie/produtos/sincronizar?produto_id=${productId}`);
      if (response.data.success) {
        toast.success('Produto enviado para o Omie!');
        fetchData();
      } else {
        toast.error(response.data.error || 'Erro ao sincronizar');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao sincronizar com Omie');
    } finally {
      setSyncingProductId(null);
    }
  };

  // Atualizar produto do Omie -> local
  const handleSyncFromOmie = async (productId) => {
    setSyncingProductId(productId);
    try {
      const response = await axios.post(`${API}/omie/produtos/atualizar-do-omie/${productId}`);
      if (response.data.success) {
        toast.success('Produto atualizado com dados do Omie!');
        fetchData();
      } else {
        toast.error(response.data.error || 'Erro ao atualizar');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao buscar dados do Omie');
    } finally {
      setSyncingProductId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        preco_base: parseFloat(formData.preco_base) || 0,
        custo: parseFloat(formData.custo) || 0,
        estoque: parseInt(formData.estoque) || 0,
        estoque_minimo: parseInt(formData.estoque_minimo) || 0,
        volume_m3: formData.volume_m3 ? parseFloat(formData.volume_m3) : null,
        tipo_madeira_id: formData.tipo_madeira_id || null,
        pecas_madeira: formData.pecas_madeira || [],
        mao_de_obra: formData.mao_de_obra ? parseFloat(formData.mao_de_obra) : null,
        pintura: formData.pintura ? parseFloat(formData.pintura) : null,
        tempo_producao_horas: formData.tempo_producao_horas ? parseFloat(formData.tempo_producao_horas) : null,
        insumos: formData.insumos ? parseFloat(formData.insumos) : null,
        margem_desejada: formData.margem_desejada ? parseFloat(formData.margem_desejada) : null,
        custo_hora_producao: formData.custo_hora_producao ? parseFloat(formData.custo_hora_producao) : null,
        nao_usa_madeira: formData.nao_usa_madeira || false
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload);
        toast.success('Produto atualizado!');
      } else {
        await axios.post(`${API}/products`, payload);
        toast.success('Produto criado!');
      }

      setDialogOpen(false);
      setEditingProduct(null);
      setVariacoes([]); // Limpar variações
      fetchData();
      setFormData({ nome: '', descricao: '', categoria: '', unidade: 'UN', preco_base: '', custo: '', estoque: '0', observacoes: '', ncm: '94036000', volume_m3: '', tipo_madeira_id: '', pecas_madeira: [], mao_de_obra: '', pintura: '', tempo_producao_horas: '', insumos: '', nao_usa_madeira: false });
      setNovaPeca({ comprimento: '', largura: '', espessura: '', quantidade: '1' });
    } catch (error) {
      console.error('Erro ao salvar produto:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar produto');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      nome: product.nome,
      descricao: product.descricao || '',
      categoria: product.categoria || '',
      unidade: product.unidade || 'UN',
      preco_base: product.preco_base.toString(),
      custo: product.custo.toString(),
      estoque: product.estoque.toString(),
      estoque_minimo: product.estoque_minimo?.toString() || '0',
      observacoes: product.observacoes || '',
      ncm: product.ncm || '94036000',
      volume_m3: product.volume_m3 ? product.volume_m3.toString() : '',
      tipo_madeira_id: product.tipo_madeira_id || '',
      pecas_madeira: product.pecas_madeira || [],
      mao_de_obra: product.mao_de_obra ? product.mao_de_obra.toString() : '',
      pintura: product.pintura ? product.pintura.toString() : '',
      tempo_producao_horas: product.tempo_producao_horas ? product.tempo_producao_horas.toString() : '',
      insumos: product.insumos ? product.insumos.toString() : '',
      nao_usa_madeira: product.nao_usa_madeira || false,
      margem_desejada: product.margem_desejada ? product.margem_desejada.toString() : '',
      custo_hora_producao: product.custo_hora_producao ? product.custo_hora_producao.toString() : ''
    });
    setNovaPeca({ comprimento: '', largura: '', espessura: '', quantidade: '1' });
    // Carregar variações do produto
    fetchVariacoes(product.id);
    setDialogOpen(true);
  };

  // Duplicar produto (cria cópia com todas as informações)
  const handleDuplicate = (product) => {
    setEditingProduct(null); // Será um novo produto
    setFormData({
      nome: `${product.nome} (Cópia)`,
      descricao: product.descricao || '',
      categoria: product.categoria || '',
      unidade: product.unidade || 'UN',
      preco_base: product.preco_base.toString(),
      custo: product.custo.toString(),
      estoque: '0', // Estoque zerado para o novo produto
      observacoes: product.observacoes || '',
      ncm: product.ncm || '94036000',
      volume_m3: product.volume_m3 ? product.volume_m3.toString() : '',
      tipo_madeira_id: product.tipo_madeira_id || '',
      pecas_madeira: product.pecas_madeira ? product.pecas_madeira.map(p => ({
        ...p,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9) // Novo ID para cada peça
      })) : [],
      mao_de_obra: product.mao_de_obra ? product.mao_de_obra.toString() : '',
      pintura: product.pintura ? product.pintura.toString() : '',
      nao_usa_madeira: product.nao_usa_madeira || false
    });
    setNovaPeca({ comprimento: '', largura: '', espessura: '', quantidade: '1' });
    setDialogOpen(true);
    toast.info('Produto duplicado! Edite as informações e salve.');
  };

  // ============ Funções para cálculo de madeira ============
  const calcularVolumePeca = (peca) => {
    const comp = parseFloat(peca.comprimento) || 0;
    const larg = parseFloat(peca.largura) || 0;
    const esp = parseFloat(peca.espessura) || 0;
    const qtd = parseInt(peca.quantidade) || 1;
    // Converte cm para m: divide por 100
    return (comp / 100) * (larg / 100) * (esp / 100) * qtd;
  };

  const calcularVolumeTotal = (pecas) => {
    return pecas.reduce((total, peca) => total + calcularVolumePeca(peca), 0);
  };

  const calcularCustoMadeira = (pecas, tipoMadeiraId) => {
    const tipo = tiposMadeira.find(t => t.id === tipoMadeiraId);
    if (!tipo) return 0;
    const volumeTotal = calcularVolumeTotal(pecas);
    return volumeTotal * tipo.preco_m3;
  };

  const calcularCustoTotal = () => {
    const custoMadeira = calcularCustoMadeira(formData.pecas_madeira, formData.tipo_madeira_id);
    const maoDeObraManual = parseFloat(formData.mao_de_obra) || 0;
    const tempoHoras = parseFloat(formData.tempo_producao_horas) || 0;
    const maoDeObraCalculada = tempoHoras * custoHora;
    const maoDeObra = maoDeObraManual > 0 ? maoDeObraManual : maoDeObraCalculada;
    const pintura = parseFloat(formData.pintura) || 0;
    const insumos = parseFloat(formData.insumos) || 0;
    return custoMadeira + maoDeObra + pintura + insumos;
  };

  const handleAddPeca = () => {
    if (!novaPeca.comprimento || !novaPeca.largura || !novaPeca.espessura) {
      toast.error('Preencha comprimento, largura e espessura');
      return;
    }
    const novaPecaComId = {
      ...novaPeca,
      id: Date.now()
    };
    const novasPecas = [...formData.pecas_madeira, novaPecaComId];
    const volumeTotal = calcularVolumeTotal(novasPecas);
    const custoMadeira = calcularCustoMadeira(novasPecas, formData.tipo_madeira_id);
    const maoDeObraManual = parseFloat(formData.mao_de_obra) || 0;
    const tempoHoras = parseFloat(formData.tempo_producao_horas) || 0;
    const maoDeObraCalculada = tempoHoras * custoHora;
    const maoDeObra = maoDeObraManual > 0 ? maoDeObraManual : maoDeObraCalculada;
    const pintura = parseFloat(formData.pintura) || 0;
    const insumos = parseFloat(formData.insumos) || 0;
    const custoTotal = custoMadeira + maoDeObra + pintura + insumos;
    
    setFormData({
      ...formData,
      pecas_madeira: novasPecas,
      volume_m3: volumeTotal.toFixed(6),
      custo: custoTotal.toFixed(2)
    });
    setNovaPeca({ comprimento: '', largura: '', espessura: '', quantidade: '1' });
    toast.success('Peça adicionada!');
  };

  const handleRemovePeca = (pecaId) => {
    const novasPecas = formData.pecas_madeira.filter(p => p.id !== pecaId);
    const volumeTotal = calcularVolumeTotal(novasPecas);
    const custoMadeira = calcularCustoMadeira(novasPecas, formData.tipo_madeira_id);
    const maoDeObraManual = parseFloat(formData.mao_de_obra) || 0;
    const tempoHoras = parseFloat(formData.tempo_producao_horas) || 0;
    const maoDeObraCalculada = tempoHoras * custoHora;
    const maoDeObra = maoDeObraManual > 0 ? maoDeObraManual : maoDeObraCalculada;
    const pintura = parseFloat(formData.pintura) || 0;
    const insumos = parseFloat(formData.insumos) || 0;
    const custoTotal = custoMadeira + maoDeObra + pintura + insumos;
    
    setFormData({
      ...formData,
      pecas_madeira: novasPecas,
      volume_m3: volumeTotal.toFixed(6),
      custo: custoTotal.toFixed(2)
    });
  };

  // Calcular custo de mão de obra baseado no tempo de produção
  const calcularCustoMaoObra = useCallback((tempoHoras) => {
    const tempo = parseFloat(tempoHoras) || 0;
    return tempo * custoHora;
  }, [custoHora]);

  // Calcular preço sugerido baseado na margem
  const calcularPrecoSugerido = useCallback((custo, margem) => {
    const custoNum = parseFloat(custo) || 0;
    const margemNum = parseFloat(margem) || 30;
    if (custoNum <= 0) return 0;
    // Preço = Custo / (1 - Margem/100)
    return custoNum / (1 - margemNum / 100);
  }, []);

  // Calcular margem real do produto
  const calcularMargemReal = useCallback((preco, custo) => {
    const precoNum = parseFloat(preco) || 0;
    const custoNum = parseFloat(custo) || 0;
    if (precoNum <= 0) return 0;
    return ((precoNum - custoNum) / precoNum) * 100;
  }, []);

  const atualizarCustoTotal = (novosDados = {}) => {
    const dados = { ...formData, ...novosDados };
    const custoMadeira = calcularCustoMadeira(dados.pecas_madeira, dados.tipo_madeira_id);
    const maoDeObraManual = parseFloat(dados.mao_de_obra) || 0;
    const tempoHoras = parseFloat(dados.tempo_producao_horas) || 0;
    const maoDeObraCalculada = tempoHoras * custoHora;
    // Usa mão de obra manual se informada, senão usa a calculada pelo tempo
    const maoDeObra = maoDeObraManual > 0 ? maoDeObraManual : maoDeObraCalculada;
    const pintura = parseFloat(dados.pintura) || 0;
    const insumos = parseFloat(dados.insumos) || 0;
    return (custoMadeira + maoDeObra + pintura + insumos).toFixed(2);
  };

  // Função para verificar se produto está incompleto
  const isProdutoIncompleto = useCallback((product) => {
    if (!product.custo || product.custo === 0) return true;
    if (!product.preco_base || product.preco_base === 0) return true;
    if (product.tipo_madeira_id && (!product.pecas_madeira || product.pecas_madeira.length === 0) && !product.volume_m3) {
      return true;
    }
    return false;
  }, []);

  // Função para verificar se produto está SEM controle de madeira
  const isProdutoSemMadeira = useCallback((product) => {
    // Se marcou que não usa madeira, não considerar como "sem madeira"
    if (product.nao_usa_madeira) return false;
    // Produto sem tipo de madeira configurado
    if (!product.tipo_madeira_id) return true;
    // Produto com tipo de madeira mas sem peças e sem volume
    if (product.tipo_madeira_id && (!product.pecas_madeira || product.pecas_madeira.length === 0) && (!product.volume_m3 || product.volume_m3 === 0)) {
      return true;
    }
    return false;
  }, []);

  // Contar produtos incompletos
  const qtdIncompletos = useMemo(() => {
    return products.filter(p => isProdutoIncompleto(p)).length;
  }, [products, isProdutoIncompleto]);

  // Contar produtos sem controle de madeira
  const qtdSemMadeira = useMemo(() => {
    return products.filter(p => isProdutoSemMadeira(p) && p.ativo !== false).length;
  }, [products, isProdutoSemMadeira]);

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este produto?')) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success('Produto excluído!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir produto');
    }
  };

  // ==================== FUNÇÕES DE VARIAÇÕES ====================
  
  // Carregar variações de um produto
  const fetchVariacoes = async (productId) => {
    try {
      const response = await axios.get(`${API}/products/${productId}/variacoes`);
      setVariacoes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar variações:', error);
      setVariacoes([]);
    }
  };

  // Abrir modal de variação para criar/editar
  const handleOpenVariacaoDialog = (variacao = null) => {
    if (variacao) {
      setEditingVariacao(variacao);
      setVariacaoForm({
        nome: variacao.nome || '',
        descricao: variacao.descricao || '',
        ativo: variacao.ativo !== false,
        tipo: variacao.tipo || 'adicional',
        valor_adicional: variacao.valor_adicional?.toString() || '',
        percentual_adicional: variacao.percentual_adicional?.toString() || '',
        tipo_madeira_id: variacao.tipo_madeira_id || '',
        pecas_madeira: variacao.pecas_madeira || [],
        tempo_adicional_horas: variacao.tempo_adicional_horas?.toString() || '',
        mao_obra_adicional: variacao.mao_obra_adicional?.toString() || '',
        pintura_adicional: variacao.pintura_adicional?.toString() || '',
        insumos_adicionais: variacao.insumos_adicionais?.toString() || '',
        ferragens_adicionais: variacao.ferragens_adicionais?.toString() || '',
        outros_custos: variacao.outros_custos?.toString() || '',
        observacoes: variacao.observacoes || ''
      });
    } else {
      setEditingVariacao(null);
      setVariacaoForm({
        nome: '',
        descricao: '',
        ativo: true,
        tipo: 'adicional',
        valor_adicional: '',
        percentual_adicional: '',
        tipo_madeira_id: formData.tipo_madeira_id || '',
        pecas_madeira: [],
        tempo_adicional_horas: '',
        mao_obra_adicional: '',
        pintura_adicional: '',
        insumos_adicionais: '',
        ferragens_adicionais: '',
        outros_custos: '',
        observacoes: ''
      });
    }
    setNovaPecaVariacao({ comprimento: '', largura: '', espessura: '', quantidade: '1' });
    setVariacaoDialogOpen(true);
  };

  // Salvar variação
  const handleSaveVariacao = async () => {
    if (!variacaoForm.nome.trim()) {
      toast.error('Nome da variação é obrigatório');
      return;
    }

    if (!editingProduct?.id) {
      toast.error('Salve o produto antes de adicionar variações');
      return;
    }

    try {
      const payload = {
        ...variacaoForm,
        valor_adicional: parseFloat(variacaoForm.valor_adicional) || 0,
        percentual_adicional: parseFloat(variacaoForm.percentual_adicional) || 0,
        tempo_adicional_horas: parseFloat(variacaoForm.tempo_adicional_horas) || 0,
        mao_obra_adicional: parseFloat(variacaoForm.mao_obra_adicional) || 0,
        pintura_adicional: parseFloat(variacaoForm.pintura_adicional) || 0,
        insumos_adicionais: parseFloat(variacaoForm.insumos_adicionais) || 0,
        ferragens_adicionais: parseFloat(variacaoForm.ferragens_adicionais) || 0,
        outros_custos: parseFloat(variacaoForm.outros_custos) || 0
      };

      if (editingVariacao) {
        await axios.put(`${API}/products/${editingProduct.id}/variacoes/${editingVariacao.id}`, payload);
        toast.success('Variação atualizada!');
      } else {
        await axios.post(`${API}/products/${editingProduct.id}/variacoes`, payload);
        toast.success('Variação criada!');
      }

      setVariacaoDialogOpen(false);
      fetchVariacoes(editingProduct.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar variação');
    }
  };

  // Excluir variação
  const handleDeleteVariacao = async (variacaoId) => {
    if (!window.confirm('Deseja excluir esta variação?')) return;
    
    try {
      await axios.delete(`${API}/products/${editingProduct.id}/variacoes/${variacaoId}`);
      toast.success('Variação excluída!');
      fetchVariacoes(editingProduct.id);
    } catch (error) {
      toast.error('Erro ao excluir variação');
    }
  };

  // Adicionar peça na variação
  const handleAddPecaVariacao = () => {
    if (!novaPecaVariacao.comprimento || !novaPecaVariacao.largura || !novaPecaVariacao.espessura) {
      toast.error('Preencha as dimensões da peça');
      return;
    }

    const novaPecaComId = {
      id: Date.now().toString(),
      comprimento: parseFloat(novaPecaVariacao.comprimento),
      largura: parseFloat(novaPecaVariacao.largura),
      espessura: parseFloat(novaPecaVariacao.espessura),
      quantidade: parseInt(novaPecaVariacao.quantidade) || 1
    };

    setVariacaoForm({
      ...variacaoForm,
      pecas_madeira: [...variacaoForm.pecas_madeira, novaPecaComId]
    });
    setNovaPecaVariacao({ comprimento: '', largura: '', espessura: '', quantidade: '1' });
  };

  // Remover peça da variação
  const handleRemovePecaVariacao = (pecaId) => {
    setVariacaoForm({
      ...variacaoForm,
      pecas_madeira: variacaoForm.pecas_madeira.filter(p => p.id !== pecaId)
    });
  };

  // Calcular custos da variação
  const calcularCustosVariacao = useCallback(() => {
    const tipoMadeiraId = variacaoForm.tipo_madeira_id || formData.tipo_madeira_id;
    const tipoMadeira = tiposMadeira.find(t => t.id === tipoMadeiraId);
    const precoM3 = tipoMadeira?.preco_m3 || 0;
    
    // Calcular volume e custo de madeira
    let volumeTotal = 0;
    variacaoForm.pecas_madeira.forEach(peca => {
      const comp = parseFloat(peca.comprimento) / 100;
      const larg = parseFloat(peca.largura) / 100;
      const esp = parseFloat(peca.espessura) / 100;
      const qtd = parseInt(peca.quantidade) || 1;
      volumeTotal += comp * larg * esp * qtd;
    });
    const custoMadeira = volumeTotal * precoM3;

    // Custo mão de obra
    const tempoHoras = parseFloat(variacaoForm.tempo_adicional_horas) || 0;
    const custoMaoObraCalculada = tempoHoras * custoHora;
    const maoObraManual = parseFloat(variacaoForm.mao_obra_adicional) || 0;
    const custoMaoObra = maoObraManual > 0 ? maoObraManual : custoMaoObraCalculada;

    // Outros custos
    const pintura = parseFloat(variacaoForm.pintura_adicional) || 0;
    const insumos = parseFloat(variacaoForm.insumos_adicionais) || 0;
    const ferragens = parseFloat(variacaoForm.ferragens_adicionais) || 0;
    const outros = parseFloat(variacaoForm.outros_custos) || 0;

    // Custo total
    const custoTotal = custoMadeira + custoMaoObra + pintura + insumos + ferragens + outros;

    // Valor de venda adicional
    const precoBaseProduto = parseFloat(formData.preco_base) || 0;
    const valorFixo = parseFloat(variacaoForm.valor_adicional) || 0;
    const percentual = parseFloat(variacaoForm.percentual_adicional) || 0;
    const valorVenda = valorFixo + (precoBaseProduto * percentual / 100);

    // Margem estimada
    const margem = valorVenda > 0 ? ((valorVenda - custoTotal) / valorVenda) * 100 : 0;

    return {
      volume_m3: volumeTotal,
      custo_madeira: custoMadeira,
      custo_mao_obra: custoMaoObra,
      custo_total: custoTotal,
      valor_venda: valorVenda,
      margem: margem
    };
  }, [variacaoForm, formData.tipo_madeira_id, formData.preco_base, tiposMadeira, custoHora]);

  // ==================== FIM FUNÇÕES VARIAÇÕES ====================

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold font-manrope tracking-tight">Produtos</h1>
            <p className="text-muted-foreground mt-1">Gerencie seu catálogo de produtos</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Botões Importar do Omie */}
            {isAdmin() && omieStatus?.ativo && (
              <>
                <Button 
                  variant="outline" 
                  onClick={abrirModalOmie}
                  disabled={importingOmie}
                  className="border-blue-500/30 hover:bg-blue-500/10"
                  data-testid="import-select-omie-button"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Importar do Omie
                </Button>
              </>
            )}
            {isAdmin() && (
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) {
                  setEditingProduct(null);
                  setFormData({ nome: '', descricao: '', categoria: '', unidade: 'UN', preco_base: '', custo: '', estoque: '0', observacoes: '', ncm: '94036000' });
                }
              }}>
                <DialogTrigger asChild>
                  <Button data-testid="add-product-button" className="shadow-[0_0_10px_rgba(52,211,153,0.2)]">
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Produto
                  </Button>
                </DialogTrigger>
              <DialogContent className="w-full max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden pb-6 mb-6 sm:mb-0 rounded-2xl sm:rounded-lg">
                <DialogHeader>
                  <DialogTitle className="font-manrope">
                    {editingProduct ? 'Editar Produto' : 'Novo Produto'}
                  </DialogTitle>
                  <DialogDescription>
                    {editingProduct ? 'Atualize as informações do produto' : 'Adicione um novo produto ao catálogo'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="nome">Nome do Produto</Label>
                      <Input
                        id="nome"
                        placeholder="Ex: Porta de Madeira Maciça"
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        required
                        data-testid="nome-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="codigo" className="flex items-center gap-1">
                        <Barcode className="h-3 w-3" />
                        SKU / Código
                      </Label>
                      <Input
                        id="codigo"
                        placeholder="SKU-001"
                        value={formData.codigo}
                        onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                        data-testid="codigo-input"
                      />
                    </div>
                  </div>

                  {/* Status Ativo/Inativo */}
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Checkbox
                      id="ativo"
                      checked={formData.ativo}
                      onCheckedChange={(checked) => setFormData({ ...formData, ativo: checked })}
                    />
                    <Label htmlFor="ativo" className="flex items-center gap-2 cursor-pointer">
                      {formData.ativo ? (
                        <>
                          <Power className="h-4 w-4 text-emerald-600" />
                          <span className="text-emerald-600 font-medium">Produto Ativo</span>
                        </>
                      ) : (
                        <>
                          <PowerOff className="h-4 w-4 text-red-600" />
                          <span className="text-red-600 font-medium">Produto Inativo</span>
                        </>
                      )}
                    </Label>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="descricao">Descrição</Label>
                    <Textarea
                      id="descricao"
                      placeholder="Descrição detalhada do produto"
                      value={formData.descricao}
                      onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                      data-testid="descricao-input"
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="categoria">Categoria</Label>
                    <Select 
                      value={formData.categoria || 'none'} 
                      onValueChange={(v) => setFormData({ ...formData, categoria: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem categoria</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="unidade">Unidade de Medida</Label>
                    <Select 
                      value={formData.unidade || 'UN'} 
                      onValueChange={(v) => setFormData({ ...formData, unidade: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UN">UN (Unidade)</SelectItem>
                        <SelectItem value="ML">Metro Linear</SelectItem>
                        <SelectItem value="M2">m² (Metro Quadrado)</SelectItem>
                        <SelectItem value="M3">m³ (Metro Cúbico)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Seção de Preços e Margem */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                      <Calculator className="h-4 w-4 text-emerald-600" />
                      Precificação e Margem
                    </h4>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="custo">Custo Total (R$)</Label>
                        <Input
                          id="custo"
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={formData.custo}
                          onChange={(e) => setFormData({ ...formData, custo: e.target.value })}
                          required
                          data-testid="custo-input"
                          className="font-medium"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="margem_desejada">Margem Desejada (%)</Label>
                        <Input
                          id="margem_desejada"
                          type="number"
                          step="1"
                          placeholder="30"
                          value={formData.margem_desejada}
                          onChange={(e) => setFormData({ ...formData, margem_desejada: e.target.value })}
                          data-testid="margem-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          <TrendingUp className="h-3 w-3 text-blue-600" />
                          Preço Sugerido
                        </Label>
                        <div className="h-9 flex items-center justify-between px-3 bg-blue-100 rounded text-blue-700 font-medium">
                          <span>R$ {calcularPrecoSugerido(formData.custo, formData.margem_desejada).toFixed(2)}</span>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 text-xs"
                            onClick={() => setFormData({ 
                              ...formData, 
                              preco_base: calcularPrecoSugerido(formData.custo, formData.margem_desejada).toFixed(2) 
                            })}
                          >
                            Usar
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="preco_base">Preço de Venda (R$)</Label>
                        <Input
                          id="preco_base"
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={formData.preco_base}
                          onChange={(e) => setFormData({ ...formData, preco_base: e.target.value })}
                          required
                          data-testid="preco-input"
                          className={`font-medium ${parseFloat(formData.preco_base) < parseFloat(formData.custo) ? 'border-red-500 bg-red-50' : ''}`}
                        />
                      </div>
                    </div>

                    {/* Alerta e Simulador de Margem */}
                    {parseFloat(formData.custo) > 0 && parseFloat(formData.preco_base) > 0 && (
                      <div className={`mt-3 p-3 rounded-lg ${
                        parseFloat(formData.preco_base) < parseFloat(formData.custo) 
                          ? 'bg-red-100 border border-red-300' 
                          : calcularMargemReal(formData.preco_base, formData.custo) < 10
                          ? 'bg-amber-100 border border-amber-300'
                          : 'bg-emerald-100 border border-emerald-300'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {parseFloat(formData.preco_base) < parseFloat(formData.custo) ? (
                              <>
                                <AlertTriangle className="h-5 w-5 text-red-600" />
                                <span className="font-medium text-red-700">PREÇO ABAIXO DO CUSTO!</span>
                              </>
                            ) : calcularMargemReal(formData.preco_base, formData.custo) < 10 ? (
                              <>
                                <AlertTriangle className="h-5 w-5 text-amber-600" />
                                <span className="font-medium text-amber-700">Margem muito baixa</span>
                              </>
                            ) : (
                              <>
                                <TrendingUp className="h-5 w-5 text-emerald-600" />
                                <span className="font-medium text-emerald-700">Margem saudável</span>
                              </>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-muted-foreground">Margem Real</div>
                            <div className={`text-lg font-bold ${
                              parseFloat(formData.preco_base) < parseFloat(formData.custo)
                                ? 'text-red-700'
                                : calcularMargemReal(formData.preco_base, formData.custo) < 10
                                ? 'text-amber-700'
                                : 'text-emerald-700'
                            }`}>
                              {calcularMargemReal(formData.preco_base, formData.custo).toFixed(1)}%
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground">
                          Lucro por unidade: <strong>R$ {(parseFloat(formData.preco_base) - parseFloat(formData.custo)).toFixed(2)}</strong>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div className="space-y-2">
                        <Label htmlFor="estoque">Estoque</Label>
                        <Input
                          id="estoque"
                          type="number"
                          placeholder="0"
                          value={formData.estoque}
                          onChange={(e) => setFormData({ ...formData, estoque: e.target.value })}
                          data-testid="estoque-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="estoque_minimo">Estoque Mínimo</Label>
                        <Input
                          id="estoque_minimo"
                          type="number"
                          placeholder="0"
                          value={formData.estoque_minimo || ''}
                          onChange={(e) => setFormData({ ...formData, estoque_minimo: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ncm">NCM (Código Fiscal)</Label>
                    <div className="flex gap-2">
                      <Select 
                        value={['44182100', '44181100', '94036000', '4418.99.00', '4418.21.00', '4418.11.00'].includes(formData.ncm?.replace(/\./g, '')) ? formData.ncm : 'custom'} 
                        onValueChange={(v) => {
                          if (v !== 'custom') {
                            setFormData({ ...formData, ncm: v });
                          }
                        }}
                      >
                        <SelectTrigger data-testid="ncm-select" className="flex-1">
                          <SelectValue placeholder="Selecione ou digite" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="44182100">44182100 - Portas, forras e alizares</SelectItem>
                          <SelectItem value="44181100">44181100 - Janelas</SelectItem>
                          <SelectItem value="94036000">94036000 - Móveis de madeira</SelectItem>
                          <SelectItem value="4418.99.00">4418.99.00 - Outras obras de marcenaria</SelectItem>
                          <SelectItem value="custom">Digitar manualmente...</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        id="ncm"
                        placeholder="Ex: 4418.21.00"
                        value={formData.ncm || ''}
                        onChange={(e) => setFormData({ ...formData, ncm: e.target.value })}
                        className="w-32"
                        data-testid="ncm-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">Código para emissão de NF-e no Omie</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="observacoes">Observações (aparece no PDF do pedido)</Label>
                    <Textarea
                      id="observacoes"
                      placeholder="Observações sobre o produto que aparecerão na impressão do pedido"
                      value={formData.observacoes}
                      onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                      data-testid="observacoes-input"
                      rows={3}
                    />
                  </div>

                  {/* Controle de Estoque de Madeira */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                      <Package className="h-4 w-4 text-amber-600" />
                      Controle de Estoque de Madeira
                    </h4>
                    
                    {/* Tipo de Madeira */}
                    <div className="space-y-2 mb-4">
                      <Label htmlFor="tipo_madeira_id">Tipo de Madeira</Label>
                      <Select 
                        value={formData.tipo_madeira_id || ''} 
                        onValueChange={(v) => {
                          const tipoId = v === 'none' ? '' : v;
                          const novoCusto = atualizarCustoTotal({ tipo_madeira_id: tipoId });
                          const volumeTotal = calcularVolumeTotal(formData.pecas_madeira);
                          setFormData({ 
                            ...formData, 
                            tipo_madeira_id: tipoId,
                            volume_m3: volumeTotal.toFixed(6),
                            custo: novoCusto
                          });
                        }}
                      >
                        <SelectTrigger data-testid="tipo-madeira-select">
                          <SelectValue placeholder="Selecione o tipo de madeira..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nenhum (sem controle)</SelectItem>
                          {tiposMadeira.map(tipo => (
                            <SelectItem key={tipo.id} value={tipo.id}>
                              {tipo.nome} - R$ {tipo.preco_m3.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/m³
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Tabela de Peças (tipo Romaneio) */}
                    {formData.tipo_madeira_id && (
                      <div className="space-y-3 p-3 bg-amber-500/5 border border-amber-500/30 rounded-lg">
                        <div className="text-sm font-medium text-amber-700">Peças de Madeira</div>
                        
                        {/* Formulário para adicionar peça */}
                        <div className="grid grid-cols-5 gap-2">
                          <div>
                            <Label className="text-xs">Comp. (cm)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              placeholder="210"
                              value={novaPeca.comprimento}
                              onChange={(e) => setNovaPeca({ ...novaPeca, comprimento: e.target.value })}
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPeca())}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Larg. (cm)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              placeholder="13"
                              value={novaPeca.largura}
                              onChange={(e) => setNovaPeca({ ...novaPeca, largura: e.target.value })}
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPeca())}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Esp. (cm)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              placeholder="3.5"
                              value={novaPeca.espessura}
                              onChange={(e) => setNovaPeca({ ...novaPeca, espessura: e.target.value })}
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPeca())}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Qtd</Label>
                            <Input
                              type="number"
                              min="1"
                              placeholder="1"
                              value={novaPeca.quantidade}
                              onChange={(e) => setNovaPeca({ ...novaPeca, quantidade: e.target.value })}
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPeca())}
                            />
                          </div>
                          <div className="flex items-end">
                            <Button type="button" size="sm" onClick={handleAddPeca} className="w-full">
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Lista de peças adicionadas */}
                        {formData.pecas_madeira.length > 0 && (
                          <div className="space-y-1 mt-2">
                            {formData.pecas_madeira.map((peca, idx) => (
                              <div key={peca.id || idx} className="flex items-center justify-between text-xs bg-background p-2 rounded border">
                                <span>
                                  <strong>{peca.quantidade}x</strong> {peca.comprimento}cm × {peca.largura}cm × {peca.espessura}cm
                                </span>
                                <div className="flex items-center gap-2">
                                  <span className="text-amber-600 font-medium">
                                    {calcularVolumePeca(peca).toFixed(6)} m³
                                  </span>
                                  <Button 
                                    type="button" 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-6 w-6 text-destructive"
                                    onClick={() => handleRemovePeca(peca.id)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Subtotal de volume */}
                        {formData.pecas_madeira.length > 0 && (
                          <div className="text-xs text-right border-t pt-2 mt-2">
                            <span className="text-muted-foreground">Volume Total: </span>
                            <span className="font-bold text-amber-600">
                              {calcularVolumeTotal(formData.pecas_madeira).toFixed(6)} m³
                            </span>
                            <span className="text-muted-foreground"> = </span>
                            <span className="font-bold text-green-600">
                              R$ {calcularCustoMadeira(formData.pecas_madeira, formData.tipo_madeira_id).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Mão de Obra, Tempo de Produção, Pintura e Insumos */}
                    {formData.tipo_madeira_id && (
                      <div className="space-y-4 mt-4">
                        {/* Tempo de produção e cálculo automático */}
                        <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="h-4 w-4 text-blue-600" />
                            <Label className="text-sm font-medium text-blue-700">Tempo de Produção</Label>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Horas</Label>
                              <Input
                                type="number"
                                step="0.5"
                                placeholder="Ex: 8"
                                value={formData.tempo_producao_horas}
                                onChange={(e) => {
                                  const novoCusto = atualizarCustoTotal({ tempo_producao_horas: e.target.value });
                                  setFormData({ ...formData, tempo_producao_horas: e.target.value, custo: novoCusto });
                                }}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Custo Calculado (R$/h: {custoHora.toFixed(2)})</Label>
                              <div className="h-9 flex items-center px-3 bg-blue-100 rounded text-blue-700 font-medium">
                                R$ {(parseFloat(formData.tempo_producao_horas || 0) * custoHora).toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-2">
                            <Label className="text-xs">Mão de Obra Manual (R$)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Opcional"
                              value={formData.mao_de_obra}
                              onChange={(e) => {
                                const novoCusto = atualizarCustoTotal({ mao_de_obra: e.target.value });
                                setFormData({ ...formData, mao_de_obra: e.target.value, custo: novoCusto });
                              }}
                            />
                            <p className="text-xs text-muted-foreground">Sobrescreve o calculado</p>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Pintura (R$)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={formData.pintura}
                              onChange={(e) => {
                                const novoCusto = atualizarCustoTotal({ pintura: e.target.value });
                                setFormData({ ...formData, pintura: e.target.value, custo: novoCusto });
                              }}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Insumos (R$)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Ferragens, cola..."
                              value={formData.insumos}
                              onChange={(e) => {
                                const novoCusto = atualizarCustoTotal({ insumos: e.target.value });
                                setFormData({ ...formData, insumos: e.target.value, custo: novoCusto });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Resumo do Custo Total */}
                    {formData.tipo_madeira_id && (formData.pecas_madeira.length > 0 || parseFloat(formData.tempo_producao_horas) > 0 || parseFloat(formData.mao_de_obra) > 0 || parseFloat(formData.pintura) > 0 || parseFloat(formData.insumos) > 0) && (
                      <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <div className="text-sm space-y-1">
                          {/* Madeira */}
                          {formData.pecas_madeira.length > 0 && (
                            <div className="flex justify-between">
                              <span>Madeira ({calcularVolumeTotal(formData.pecas_madeira).toFixed(4)} m³):</span>
                              <span>R$ {calcularCustoMadeira(formData.pecas_madeira, formData.tipo_madeira_id).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                            </div>
                          )}
                          {/* Mão de Obra - calculada ou manual */}
                          {(parseFloat(formData.tempo_producao_horas) > 0 || parseFloat(formData.mao_de_obra) > 0) && (
                            <div className="flex justify-between">
                              <span>Mão de Obra{parseFloat(formData.mao_de_obra) > 0 ? ' (manual)' : ` (${formData.tempo_producao_horas}h)`}:</span>
                              <span>R$ {(parseFloat(formData.mao_de_obra) > 0 
                                ? parseFloat(formData.mao_de_obra) 
                                : parseFloat(formData.tempo_producao_horas || 0) * custoHora
                              ).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                            </div>
                          )}
                          {/* Pintura */}
                          {parseFloat(formData.pintura) > 0 && (
                            <div className="flex justify-between">
                              <span>Pintura:</span>
                              <span>R$ {parseFloat(formData.pintura).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                            </div>
                          )}
                          {/* Insumos */}
                          {parseFloat(formData.insumos) > 0 && (
                            <div className="flex justify-between">
                              <span>Insumos:</span>
                              <span>R$ {parseFloat(formData.insumos).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                            </div>
                          )}
                          {/* Total */}
                          <div className="flex justify-between font-bold text-green-700 border-t pt-1 mt-1">
                            <span>CUSTO TOTAL:</span>
                            <span>R$ {calcularCustoTotal().toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Volume manual (caso não use a tabela) */}
                    {!formData.tipo_madeira_id && (
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div className="space-y-2">
                          <Label htmlFor="volume_m3">Volume por Unidade (m³)</Label>
                          <Input
                            id="volume_m3"
                            type="number"
                            step="0.0001"
                            placeholder="0.0000"
                            value={formData.volume_m3}
                            onChange={(e) => setFormData({ ...formData, volume_m3: e.target.value })}
                            data-testid="volume-m3-input"
                          />
                        </div>
                      </div>
                    )}

                    {/* ==================== SEÇÃO DE VARIAÇÕES ==================== */}
                    <div className="mt-6 p-4 bg-purple-500/5 border border-purple-500/30 rounded-lg">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-5 w-5 text-purple-600" />
                          <h3 className="text-sm font-semibold text-purple-700">Variações do Produto</h3>
                          {variacoes.length > 0 && (
                            <Badge variant="secondary" className="text-xs">{variacoes.length}</Badge>
                          )}
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="border-purple-500/50 text-purple-600 hover:bg-purple-500/10"
                          onClick={async () => {
                            // Se o produto ainda não foi salvo, salvar primeiro
                            if (!editingProduct) {
                              if (!formData.nome.trim()) {
                                toast.error('Preencha o nome do produto antes de adicionar variações');
                                return;
                              }
                              // Salvar o produto primeiro
                              try {
                                const payload = {
                                  nome: formData.nome,
                                  descricao: formData.descricao,
                                  categoria: formData.categoria,
                                  unidade: formData.unidade,
                                  preco_base: parseFloat(formData.preco_base) || 0,
                                  custo: parseFloat(formData.custo) || 0,
                                  estoque: parseInt(formData.estoque) || 0,
                                  estoque_minimo: parseInt(formData.estoque_minimo) || 0,
                                  observacoes: formData.observacoes,
                                  ncm: formData.ncm,
                                  volume_m3: parseFloat(formData.volume_m3) || null,
                                  tipo_madeira_id: formData.tipo_madeira_id || null,
                                  pecas_madeira: formData.pecas_madeira || [],
                                  mao_de_obra: parseFloat(formData.mao_de_obra) || 0,
                                  pintura: parseFloat(formData.pintura) || 0,
                                  tempo_producao_horas: parseFloat(formData.tempo_producao_horas) || 0,
                                  insumos: parseFloat(formData.insumos) || 0,
                                  nao_usa_madeira: formData.nao_usa_madeira || false,
                                  margem_desejada: parseFloat(formData.margem_desejada) || null,
                                  custo_hora_producao: parseFloat(formData.custo_hora_producao) || null
                                };
                                const response = await axios.post(`${API}/products`, payload);
                                const novoProduto = response.data;
                                setEditingProduct(novoProduto);
                                toast.success('Produto salvo! Agora adicione as variações.');
                                // Abrir o dialog de variação
                                handleOpenVariacaoDialog();
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Erro ao salvar produto');
                                return;
                              }
                            } else {
                              handleOpenVariacaoDialog();
                            }
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Adicionar Variação
                        </Button>
                      </div>

                      {/* Mensagem para produto não salvo */}
                      {!editingProduct && (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Ao clicar em &quot;Adicionar Variação&quot;, o produto será salvo automaticamente.
                        </p>
                      )}

                      {/* Lista de variações */}
                      {editingProduct && variacoes.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Nenhuma variação cadastrada. Clique em &quot;Adicionar Variação&quot; para criar.
                        </p>
                      )}
                      
                      {variacoes.length > 0 && (
                        <div className="space-y-2">
                          {variacoes.map((variacao) => (
                            <div
                              key={variacao.id}
                              className={`p-3 rounded-lg border ${
                                variacao.ativo
                                  ? 'bg-background border-purple-500/30'
                                  : 'bg-muted/50 border-muted opacity-60'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{variacao.nome}</span>
                                  <Badge
                                    variant="outline"
                                    className={`text-xs ${
                                      variacao.tipo === 'obrigatoria' ? 'border-red-500 text-red-600' :
                                      variacao.tipo === 'opcional' ? 'border-blue-500 text-blue-600' :
                                      variacao.tipo === 'substituicao' ? 'border-amber-500 text-amber-600' :
                                      'border-green-500 text-green-600'
                                    }`}
                                  >
                                    {variacao.tipo}
                                  </Badge>
                                  {!variacao.ativo && (
                                    <Badge variant="secondary" className="text-xs">Inativo</Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7"
                                    onClick={() => handleOpenVariacaoDialog(variacao)}
                                  >
                                    <Edit className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-destructive"
                                    onClick={() => handleDeleteVariacao(variacao.id)}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </div>
                              
                              {/* Resumo da variação */}
                              <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
                                {(variacao.valor_adicional > 0 || variacao.percentual_adicional > 0) && (
                                  <span className="text-green-600">
                                    + R$ {(variacao.valor_venda_adicional || 0).toFixed(2)} venda
                                  </span>
                                )}
                                {variacao.custo_total_adicional > 0 && (
                                  <span className="text-amber-600">
                                    + R$ {variacao.custo_total_adicional.toFixed(2)} custo
                                  </span>
                                )}
                                {variacao.volume_m3_adicional > 0 && (
                                  <span className="text-blue-600">
                                    + {variacao.volume_m3_adicional.toFixed(4)} m³
                                  </span>
                                )}
                                {variacao.tempo_adicional_horas > 0 && (
                                  <span className="text-purple-600">
                                    + {variacao.tempo_adicional_horas}h produção
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* ==================== FIM SEÇÃO VARIAÇÕES ==================== */}
                  </div>

                  <Button type="submit" className="w-full" data-testid="submit-product">
                    {editingProduct ? 'Atualizar' : 'Criar Produto'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            )}

            {/* ==================== MODAL DE VARIAÇÃO ==================== */}
            <Dialog open={variacaoDialogOpen} onOpenChange={setVariacaoDialogOpen}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-purple-600" />
                    {editingVariacao ? 'Editar Variação' : 'Nova Variação'}
                  </DialogTitle>
                  <DialogDescription>
                    Produto base: <strong>{formData.nome}</strong>
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                  {/* Informações Básicas */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-purple-700">Informações da Variação</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nome da Variação *</Label>
                        <Input
                          placeholder="Ex: Forra larga"
                          value={variacaoForm.nome}
                          onChange={(e) => setVariacaoForm({ ...variacaoForm, nome: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Tipo</Label>
                        <Select
                          value={variacaoForm.tipo}
                          onValueChange={(v) => setVariacaoForm({ ...variacaoForm, tipo: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="adicional">Adicional</SelectItem>
                            <SelectItem value="substituicao">Substituição</SelectItem>
                            <SelectItem value="obrigatoria">Obrigatória</SelectItem>
                            <SelectItem value="opcional">Opcional</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Descrição</Label>
                      <Textarea
                        placeholder="Descrição da variação..."
                        value={variacaoForm.descricao}
                        onChange={(e) => setVariacaoForm({ ...variacaoForm, descricao: e.target.value })}
                        rows={2}
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="variacao-ativo"
                        checked={variacaoForm.ativo}
                        onCheckedChange={(checked) => setVariacaoForm({ ...variacaoForm, ativo: checked })}
                      />
                      <Label htmlFor="variacao-ativo" className="cursor-pointer">Variação ativa</Label>
                    </div>
                  </div>

                  {/* Impacto no Preço */}
                  <div className="p-4 bg-green-500/5 border border-green-500/30 rounded-lg space-y-4">
                    <h4 className="text-sm font-semibold text-green-700">Impacto no Preço de Venda</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Valor Adicional Fixo (R$)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0,00"
                          value={variacaoForm.valor_adicional}
                          onChange={(e) => setVariacaoForm({ ...variacaoForm, valor_adicional: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Percentual Adicional (%)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="0"
                          value={variacaoForm.percentual_adicional}
                          onChange={(e) => setVariacaoForm({ ...variacaoForm, percentual_adicional: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Peças de Madeira da Variação */}
                  <div className="p-4 bg-amber-500/5 border border-amber-500/30 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-amber-700">Peças de Madeira Adicionais</h4>
                      <Select
                        value={variacaoForm.tipo_madeira_id || formData.tipo_madeira_id || ''}
                        onValueChange={(v) => setVariacaoForm({ ...variacaoForm, tipo_madeira_id: v })}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Tipo de madeira" />
                        </SelectTrigger>
                        <SelectContent>
                          {tiposMadeira.map((tipo) => (
                            <SelectItem key={tipo.id} value={tipo.id}>
                              {tipo.nome} (R$ {tipo.preco_m3}/m³)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Formulário para adicionar peça */}
                    <div className="grid grid-cols-5 gap-2">
                      <div>
                        <Label className="text-xs">Comp. (cm)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="210"
                          value={novaPecaVariacao.comprimento}
                          onChange={(e) => setNovaPecaVariacao({ ...novaPecaVariacao, comprimento: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Larg. (cm)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="13"
                          value={novaPecaVariacao.largura}
                          onChange={(e) => setNovaPecaVariacao({ ...novaPecaVariacao, largura: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Esp. (cm)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="3.5"
                          value={novaPecaVariacao.espessura}
                          onChange={(e) => setNovaPecaVariacao({ ...novaPecaVariacao, espessura: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Qtd</Label>
                        <Input
                          type="number"
                          min="1"
                          value={novaPecaVariacao.quantidade}
                          onChange={(e) => setNovaPecaVariacao({ ...novaPecaVariacao, quantidade: e.target.value })}
                        />
                      </div>
                      <div className="flex items-end">
                        <Button type="button" size="sm" onClick={handleAddPecaVariacao} className="w-full">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Lista de peças da variação */}
                    {variacaoForm.pecas_madeira.length > 0 && (
                      <div className="space-y-1">
                        {variacaoForm.pecas_madeira.map((peca, idx) => (
                          <div key={peca.id || idx} className="flex items-center justify-between text-xs bg-background p-2 rounded border">
                            <span>
                              <strong>{peca.quantidade}x</strong> {peca.comprimento}cm × {peca.largura}cm × {peca.espessura}cm
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-amber-600 font-medium">
                                {calcularVolumePeca(peca).toFixed(6)} m³
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-destructive"
                                onClick={() => handleRemovePecaVariacao(peca.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Tempo e Mão de Obra */}
                  <div className="p-4 bg-blue-500/5 border border-blue-500/30 rounded-lg space-y-4">
                    <h4 className="text-sm font-semibold text-blue-700">Tempo de Produção Adicional</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Horas Adicionais</Label>
                        <Input
                          type="number"
                          step="0.5"
                          placeholder="0"
                          value={variacaoForm.tempo_adicional_horas}
                          onChange={(e) => setVariacaoForm({ ...variacaoForm, tempo_adicional_horas: e.target.value })}
                        />
                        {variacaoForm.tempo_adicional_horas && custoHora > 0 && (
                          <p className="text-xs text-muted-foreground">
                            = R$ {(parseFloat(variacaoForm.tempo_adicional_horas) * custoHora).toFixed(2)} mão de obra
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Mão de Obra Manual (R$)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Sobrescreve o cálculo"
                          value={variacaoForm.mao_obra_adicional}
                          onChange={(e) => setVariacaoForm({ ...variacaoForm, mao_obra_adicional: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Outros Custos */}
                  <div className="p-4 bg-slate-500/5 border border-slate-500/30 rounded-lg space-y-4">
                    <h4 className="text-sm font-semibold text-slate-700">Outros Custos Adicionais</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Pintura (R$)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0,00"
                          value={variacaoForm.pintura_adicional}
                          onChange={(e) => setVariacaoForm({ ...variacaoForm, pintura_adicional: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Insumos (R$)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0,00"
                          value={variacaoForm.insumos_adicionais}
                          onChange={(e) => setVariacaoForm({ ...variacaoForm, insumos_adicionais: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Ferragens (R$)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0,00"
                          value={variacaoForm.ferragens_adicionais}
                          onChange={(e) => setVariacaoForm({ ...variacaoForm, ferragens_adicionais: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Outros (R$)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0,00"
                          value={variacaoForm.outros_custos}
                          onChange={(e) => setVariacaoForm({ ...variacaoForm, outros_custos: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Observações</Label>
                      <Textarea
                        placeholder="Observações adicionais..."
                        value={variacaoForm.observacoes}
                        onChange={(e) => setVariacaoForm({ ...variacaoForm, observacoes: e.target.value })}
                        rows={2}
                      />
                    </div>
                  </div>

                  {/* Resumo da Variação */}
                  {(() => {
                    const custos = calcularCustosVariacao();
                    return (
                      <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                        <h4 className="text-sm font-semibold text-purple-700 mb-3">Resumo da Variação</h4>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Custo Madeira</p>
                            <p className="font-medium text-amber-600">+ R$ {custos.custo_madeira.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Custo Mão de Obra</p>
                            <p className="font-medium text-blue-600">+ R$ {custos.custo_mao_obra.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Volume Madeira</p>
                            <p className="font-medium text-amber-600">+ {custos.volume_m3.toFixed(4)} m³</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Custo Total</p>
                            <p className="font-bold text-red-600">+ R$ {custos.custo_total.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Valor Venda</p>
                            <p className="font-bold text-green-600">+ R$ {custos.valor_venda.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Margem Estimada</p>
                            <p className={`font-bold ${custos.margem >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {custos.margem.toFixed(1)}%
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setVariacaoDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleSaveVariacao} className="bg-purple-600 hover:bg-purple-700">
                    {editingVariacao ? 'Atualizar Variação' : 'Criar Variação'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            {/* ==================== FIM MODAL VARIAÇÃO ==================== */}
          </div>
        </div>

        {/* Barra de Pesquisa e Filtro */}
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Pesquisar por nome..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterCategory || 'all'} onValueChange={(v) => setFilterCategory(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-auto min-w-[160px]">
                  <Filter className="h-4 w-4 mr-2 flex-shrink-0" />
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas categorias</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Filtro de incompletos */}
              {qtdIncompletos > 0 && (
                <Button
                  variant={filterIncompletos ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterIncompletos(!filterIncompletos)}
                  className={filterIncompletos ? "bg-amber-500 hover:bg-amber-600" : "border-amber-500/50 text-amber-600 hover:bg-amber-500/10"}
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Incompletos ({qtdIncompletos})
                </Button>
              )}

              {/* Filtro de produtos SEM controle de madeira */}
              {qtdSemMadeira > 0 && (
                <Button
                  variant={filterSemMadeira ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterSemMadeira(!filterSemMadeira)}
                  className={filterSemMadeira ? "bg-orange-500 hover:bg-orange-600" : "border-orange-500/50 text-orange-600 hover:bg-orange-500/10"}
                >
                  <Package className="h-4 w-4 mr-2" />
                  Sem Madeira ({qtdSemMadeira})
                </Button>
              )}
              
              {/* Filtro de status ativo/inativo */}
              <Select value={filterAtivo} onValueChange={setFilterAtivo}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="ativos">
                    <div className="flex items-center gap-2">
                      <Power className="h-3 w-3 text-emerald-600" />
                      Ativos
                    </div>
                  </SelectItem>
                  <SelectItem value="inativos">
                    <div className="flex items-center gap-2">
                      <PowerOff className="h-3 w-3 text-red-600" />
                      Inativos
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Products List */}
        <div className="space-y-3">
          {products
            .filter(p => {
              const matchSearch = !searchTerm || searchMatch(p.nome, searchTerm) || (p.codigo && p.codigo.toLowerCase().includes(searchTerm.toLowerCase()));
              const matchCategory = !filterCategory || p.categoria === filterCategory;
              const matchIncompleto = !filterIncompletos || isProdutoIncompleto(p);
              const matchSemMadeira = !filterSemMadeira || isProdutoSemMadeira(p);
              const matchAtivo = filterAtivo === 'todos' || 
                                (filterAtivo === 'ativos' && p.ativo !== false) || 
                                (filterAtivo === 'inativos' && p.ativo === false);
              return matchSearch && matchCategory && matchIncompleto && matchSemMadeira && matchAtivo;
            })
            .map(product => {
            const margem = ((product.preco_base - product.custo) / product.preco_base * 100).toFixed(1);
            const isInativo = product.ativo === false;
            const semMadeira = isProdutoSemMadeira(product);
            
            // Verificar dados incompletos
            const dadosIncompletos = [];
            if (!product.custo || product.custo === 0) dadosIncompletos.push('Custo');
            if (!product.preco_base || product.preco_base === 0) dadosIncompletos.push('Preço');
            if (!product.tipo_madeira_id && (!product.pecas_madeira || product.pecas_madeira.length === 0)) {
              // Se não tem tipo de madeira E não tem peças cadastradas, pode ser incompleto
              // Mas só marca se for um produto que deveria ter (ex: categoria móveis/portas)
            }
            if (product.tipo_madeira_id && (!product.pecas_madeira || product.pecas_madeira.length === 0) && !product.volume_m3) {
              dadosIncompletos.push('Peças/Volume');
            }
            const temDadosIncompletos = dadosIncompletos.length > 0;
            
            return (
              <Card 
                key={product.id} 
                className={`border-border hover:border-primary/50 transition-colors ${semMadeira ? 'border-l-4 border-l-orange-500 bg-orange-500/5' : ''} ${temDadosIncompletos && !semMadeira ? 'border-l-4 border-l-amber-500' : ''} ${isInativo ? 'opacity-60 bg-muted/30' : ''}`} 
                data-testid={`product-${product.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Package className={`h-5 w-5 ${isInativo ? 'text-muted-foreground' : 'text-primary'}`} />
                        <h3 className={`font-manrope text-lg font-bold ${isInativo ? 'text-muted-foreground' : ''}`}>{product.nome}</h3>
                        {product.codigo && (
                          <Badge variant="outline" className="text-xs font-mono">
                            {product.codigo}
                          </Badge>
                        )}
                        {isInativo && (
                          <Badge variant="secondary" className="bg-red-100 text-red-700 border-red-300">
                            <PowerOff className="h-3 w-3 mr-1" />
                            Inativo
                          </Badge>
                        )}
                        {temDadosIncompletos && (
                          <Badge 
                            variant="outline" 
                            className="bg-amber-500/10 text-amber-600 border-amber-500/30 gap-1 cursor-help"
                            title={`Faltam: ${dadosIncompletos.join(', ')}`}
                          >
                            <AlertTriangle className="h-3 w-3" />
                            Incompleto
                          </Badge>
                        )}
                        {semMadeira && !isInativo && (
                          <Badge 
                            variant="outline" 
                            className="bg-orange-500/10 text-orange-600 border-orange-500/30 gap-1 cursor-pointer animate-pulse"
                            title="Clique para configurar o controle de madeira"
                            onClick={() => handleEdit(product)}
                          >
                            <Package className="h-3 w-3" />
                            Sem Madeira
                          </Badge>
                        )}
                        {product.categoria && (
                          <span className="text-xs px-2 py-1 bg-muted rounded">{product.categoria}</span>
                        )}
                        {product.unidade && product.unidade !== 'UN' && (
                          <span className="text-xs px-2 py-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded">
                            {product.unidade === 'ML' ? 'Metro Linear' : product.unidade === 'M2' ? 'm²' : product.unidade === 'M3' ? 'm³' : product.unidade}
                          </span>
                        )}
                        {product.sincronizado_omie && (
                          <Badge variant="secondary" className="bg-green-500/10 text-green-600 dark:text-green-400 gap-1">
                            <Cloud className="h-3 w-3" />
                            Omie
                          </Badge>
                        )}
                      </div>
                      
                      {product.descricao && (
                        <p className="text-sm text-muted-foreground mb-3">{product.descricao}</p>
                      )}
                      
                      <div className="flex gap-6 text-sm flex-wrap">
                        <div>
                          <span className="text-muted-foreground">Custo: </span>
                          <span className="font-mono">R$ {product.custo.toFixed(2)}{product.unidade && product.unidade !== 'UN' ? `/${product.unidade === 'ML' ? 'm' : product.unidade === 'M2' ? 'm²' : product.unidade === 'M3' ? 'm³' : ''}` : ''}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Preço: </span>
                          <span className="font-mono font-bold text-primary">R$ {product.preco_base.toFixed(2)}{product.unidade && product.unidade !== 'UN' ? `/${product.unidade === 'ML' ? 'm' : product.unidade === 'M2' ? 'm²' : product.unidade === 'M3' ? 'm³' : ''}` : ''}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Margem: </span>
                          <span className="font-mono text-primary">{margem}%</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Estoque: </span>
                          <span className={`font-mono ${product.estoque <= 0 ? 'text-red-500 font-bold' : product.estoque <= (product.estoque_minimo || 5) ? 'text-amber-500' : ''}`}>
                            {product.estoque} {product.unidade === 'ML' ? 'm' : product.unidade === 'M2' ? 'm²' : product.unidade === 'M3' ? 'm³' : 'un'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <ShoppingCart className="h-3 w-3 text-muted-foreground" />
                          <span className="text-muted-foreground">Vendido: </span>
                          <span className="font-mono text-green-600 font-bold">
                            {vendasPorProduto[product.id] || 0}
                          </span>
                        </div>
                      </div>

                      {product.variacoes && product.variacoes.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs text-muted-foreground mb-2">Variações disponíveis:</p>
                          <div className="flex flex-wrap gap-2">
                            {product.variacoes.map((v, idx) => (
                              <span key={idx} className="text-xs px-2 py-1 bg-primary/10 text-primary rounded">
                                {v.tamanho}
                                {v.tipo_madeira && ` - ${v.tipo_madeira}`}
                                {v.preco_adicional > 0 && ` (+R$ ${v.preco_adicional})`}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {isAdmin() && (
                      <div className="flex flex-col gap-1">
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDuplicate(product)}
                            data-testid={`duplicate-product-${product.id}`}
                            title="Duplicar produto"
                          >
                            <Copy className="h-4 w-4 text-blue-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(product)}
                            data-testid={`edit-product-${product.id}`}
                            title="Editar produto"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(product.id)}
                            data-testid={`delete-product-${product.id}`}
                            title="Excluir produto"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                        {omieStatus?.connected && (
                          <div className="flex gap-1">
                            {product.codigo_omie ? (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleSyncToOmie(product.id)}
                                  disabled={syncingProductId === product.id}
                                  data-testid={`sync-to-omie-${product.id}`}
                                  title="Enviar para Omie"
                                  className="h-7 w-7"
                                >
                                  {syncingProductId === product.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Upload className="h-3 w-3 text-blue-500" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleSyncFromOmie(product.id)}
                                  disabled={syncingProductId === product.id}
                                  data-testid={`sync-from-omie-${product.id}`}
                                  title="Atualizar do Omie"
                                  className="h-7 w-7"
                                >
                                  {syncingProductId === product.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <RefreshCw className="h-3 w-3 text-green-500" />
                                  )}
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSyncToOmie(product.id)}
                                disabled={syncingProductId === product.id}
                                data-testid={`sync-to-omie-${product.id}`}
                                title="Cadastrar no Omie"
                                className="h-7 text-xs"
                              >
                                {syncingProductId === product.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                ) : (
                                  <Cloud className="h-3 w-3 mr-1" />
                                )}
                                Omie
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {products.length === 0 && (
          <Card className="border-border">
            <CardContent className="py-16 text-center">
              <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">Nenhum produto cadastrado</p>
              {isAdmin() && (
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Primeiro Produto
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Modal de Seleção de Produtos do Omie */}
        <Dialog open={omieDialogOpen} onOpenChange={setOmieDialogOpen}>
          <DialogContent className="w-full max-w-4xl max-h-[85vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="font-manrope flex items-center gap-2">
                <Cloud className="h-5 w-5 text-blue-500" />
                Importar Produtos do Omie
              </DialogTitle>
              <DialogDescription>
                Selecione os produtos que deseja importar ({omieTotal} produtos disponíveis)
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Barra de pesquisa e ações */}
              <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar em todos os produtos... (mín. 2 caracteres)"
                    value={omieSearchTerm}
                    onChange={(e) => handleOmieSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleSelecionarTodos}
                >
                  {omieProdutos.every(p => omieSelecionados.includes(p.codigo_produto)) 
                    ? 'Desmarcar Todos' 
                    : 'Selecionar Todos'}
                </Button>
              </div>

              {/* Lista de produtos */}
              <ScrollArea className="h-[400px] border rounded-md p-2">
                {omieLoading ? (
                  <div className="flex flex-col items-center justify-center h-full gap-3">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                    <span className="text-muted-foreground text-center">
                      {omieSearchTerm 
                        ? `Buscando "${omieSearchTerm}" em todos os ${omieTotal || 1861} produtos...\n(pode levar até 30 segundos)`
                        : 'Carregando produtos...'}
                    </span>
                  </div>
                ) : omieProdutos.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
                    <Package className="h-12 w-12" />
                    <span>Nenhum produto encontrado</span>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {omieProdutos.map((produto) => (
                      <div
                        key={produto.codigo_produto}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          omieSelecionados.includes(produto.codigo_produto)
                            ? 'bg-primary/10 border border-primary/30'
                            : 'hover:bg-muted/50'
                        }`}
                        onClick={() => toggleSelecionado(produto.codigo_produto)}
                      >
                        <Checkbox
                          checked={omieSelecionados.includes(produto.codigo_produto)}
                          onCheckedChange={() => toggleSelecionado(produto.codigo_produto)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{produto.descricao}</p>
                          <div className="flex gap-2 text-xs text-muted-foreground">
                            <span>Código: {produto.codigo}</span>
                            {produto.ncm && <span>NCM: {produto.ncm}</span>}
                            {produto.valor_unitario > 0 && (
                              <span>R$ {produto.valor_unitario.toFixed(2)}</span>
                            )}
                          </div>
                        </div>
                        {omieSelecionados.includes(produto.codigo_produto) && (
                          <Check className="h-5 w-5 text-primary" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              {/* Paginação (esconder quando em modo de busca) */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {omieSearchTerm ? (
                    <span>{omieProdutos.length} resultado(s) encontrado(s) | {omieSelecionados.length} selecionado(s)</span>
                  ) : (
                    <span>Página {omiePagina} de {omieTotalPaginas} | {omieSelecionados.length} selecionado(s)</span>
                  )}
                </div>
                {!omieSearchTerm && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => carregarProdutosOmie(omiePagina - 1)}
                      disabled={omiePagina <= 1 || omieLoading}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => carregarProdutosOmie(omiePagina + 1)}
                      disabled={omiePagina >= omieTotalPaginas || omieLoading}
                    >
                      Próxima
                    </Button>
                  </div>
                )}
              </div>

              {/* Botão de importar */}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setOmieDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={importarSelecionados}
                  disabled={omieSelecionados.length === 0 || importingOmie}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {importingOmie ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Importar {omieSelecionados.length} Produto{omieSelecionados.length !== 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}